import React, { useState, useMemo, useCallback, useEffect } from "react";
import InactiveUserIcon from "../../../assets/svg/inactive-user-icon";
import RightArrowIcon from "../../../assets/svg/right-arrow-icon";
import {
  useGetLeadLostStageReasonsQuery,
  useGetLeadCancellationReasonsQuery,
  useUpdateLeadStageMutation,
} from "../../../redux/services/crmApi";
import SearchableDropdown from "../../../components/SearchableDropdown/SearchableDropdown";
import TextArea from "../../../components/TextArea/TextArea";
import Button from "../../../components/Button/Button";
import { customToast } from "../../../utils/custom-toast/custom-toast";
import { trackEvents } from "../../../utils/amplitude/amplitude";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { getLostReasonDisplayName } from "../../../utils/constants/mappings";
import { getClientDomain } from "../../../utils/functions/client-code";

const VERBAFLO_PRIMARY_REASONS = [
  { value: "Not Interested in AI", label: "Not Interested in AI" },
  {
    value: "Not the right timing (Future Churn)",
    label: "Not the right timing (Future Churn)",
  },
  {
    value: "Did not like the Product",
    label: "Did not like the Product",
  },
  { value: "Product Misfit", label: "Product Misfit" },
  { value: "Lost to competitor", label: "Lost to competitor" },
  { value: "Budget Constraint", label: "Budget Constraint" },
];

const REASON_OPTION_CLASS =
  "block overflow-hidden text-ellipsis whitespace-nowrap max-w-full text-md text-heading-text dark:text-heading-text-dk";

const REASON_OPTION_CLASS_FULL =
  "block whitespace-nowrap text-md text-heading-text dark:text-heading-text-dk";

const LeadStageChangeToLostModal = ({
  leadId,
  open,
  onOpenChange,
  currentStage,
  newStage,
  stageColorMap,
  stageNameMap,
  onStageUpdate,
  onClose,
}: {
  leadId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentStage: string;
  newStage: string;
  stageColorMap: Record<string, string>;
  stageNameMap: Record<string, string>;
  onStageUpdate: (
    stage: string,
    reasonData: {
      lost_primary_reason: string | null;
      lost_reason: string | null;
      reason_text: string | null;
      cancelled_primary_reason?: string | null;
      cancelled_reason?: string | null;
      cancellation_reason_text?: string | null;
    }
  ) => void;
  onClose?: () => void;
}) => {
  const [selectedPrimaryReason, setSelectedPrimaryReason] = useState<
    string | null
  >(null);
  const [selectedSecondaryReason, setSelectedSecondaryReason] = useState<
    string | null
  >(null);
  const [reasonText, setReasonText] = useState<string>("");

  const [updateLeadStage, { isLoading: isUpdatingLeadStage }] =
    useUpdateLeadStageMutation();
  const orgId =
    useSelector((state: RootState) => state?.auth?.client?.client_code) ||
    "unknown";
  const userId =
    useSelector((state: RootState) => state?.auth?.user?._id) || "unknown";
  const isVerbafloClient = orgId === "verbaflo";

  const isCancelledStage = newStage?.toLowerCase() === "cancelled";

  const { data: lostReasonsRaw, isLoading: isLostReasonsLoading } =
    useGetLeadLostStageReasonsQuery({ stage_key: newStage }, { skip: isCancelledStage || !open });

  const {
    data: cancellationReasonsRaw,
    isLoading: isCancellationReasonsLoading,
  } = useGetLeadCancellationReasonsQuery({ stage_key: "cancelled" }, { skip: !isCancelledStage || !open });

  const reasonsData = isCancelledStage
    ? cancellationReasonsRaw
    : lostReasonsRaw;
  const isReasonsLoading = isCancelledStage
    ? isCancellationReasonsLoading
    : isLostReasonsLoading;

  useEffect(() => {
    if (!open) {
      setSelectedPrimaryReason(null);
      setSelectedSecondaryReason(null);
      setReasonText("");
    }
  }, [open]);


  const isOtherReasonSelected = useMemo(
    () =>
      selectedPrimaryReason?.toLowerCase() === "other" ||
      selectedPrimaryReason?.toLowerCase() === "others" ||
      (selectedPrimaryReason?.toLowerCase() === "others (please specify)" &&
        getClientDomain() === "mansionstudent"),
    [selectedPrimaryReason]
  );

  const primaryReasonOptions = useMemo(() => {
    if (isVerbafloClient) {
      return VERBAFLO_PRIMARY_REASONS.map((reason) => ({
        content: (
          <span className={REASON_OPTION_CLASS}>
            {getLostReasonDisplayName(reason.label, orgId)}
          </span>
        ),
        value: reason.value,
      }));
    }
    return (reasonsData?.primary_reasons || []).map((reason: string) => ({
      content: (
        <span className={REASON_OPTION_CLASS}>
          {getLostReasonDisplayName(reason, orgId)}
        </span>
      ),
      value: reason,
    }));
  }, [reasonsData, isVerbafloClient, orgId]);

  const secondaryReasonOptions = useMemo(() => {
    if (!selectedPrimaryReason || !reasonsData?.mapping) return [];

    const mapping = reasonsData.mapping as Record<string, string[]>;
    const reasons =
      mapping[selectedPrimaryReason] ??
      Object.entries(mapping).find(
        ([key]) => key.toLowerCase() === selectedPrimaryReason.toLowerCase()
      )?.[1] ??
      [];

    return reasons.map((reason: string) => ({
      content: (
        <span className={REASON_OPTION_CLASS_FULL}>
          {getLostReasonDisplayName(reason, orgId)}
        </span>
      ),
      value: reason,
    }));
  }, [selectedPrimaryReason, reasonsData, orgId]);

  const hasSecondaryOptions = secondaryReasonOptions.length > 0;

  const handlePrimaryReasonChange = useCallback((value: string | null) => {
    setSelectedPrimaryReason(value);
    setSelectedSecondaryReason(null);
    setReasonText("");
  }, []);

  const handleReasonTextChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setReasonText(e.target.value);
    },
    []
  );

  const isFormValid = useCallback(() => {
    if (!selectedPrimaryReason) return false;
    if (isOtherReasonSelected) return !!reasonText.trim();
    if (!isVerbafloClient && hasSecondaryOptions)
      return !!selectedSecondaryReason;
    return true;
  }, [
    selectedPrimaryReason,
    isOtherReasonSelected,
    reasonText,
    isVerbafloClient,
    hasSecondaryOptions,
    selectedSecondaryReason,
  ]);

  const buildReasonPayload = useCallback(() => {
    const secondaryValue =
      isOtherReasonSelected || isVerbafloClient ? "" : selectedSecondaryReason;
    const freeTextValue = isOtherReasonSelected ? reasonText : null;

    if (isCancelledStage) {
      return {
        lost_primary_reason: null,
        lost_reason: null,
        reason_text: null,
        cancelled_primary_reason: selectedPrimaryReason,
        cancelled_reason: secondaryValue,
        cancellation_reason_text: freeTextValue,
      };
    }

    return {
      lost_primary_reason: selectedPrimaryReason,
      lost_reason: secondaryValue,
      reason_text: freeTextValue,
      cancelled_primary_reason: null,
      cancelled_reason: null,
      cancellation_reason_text: null,
    };
  }, [
    isCancelledStage,
    selectedPrimaryReason,
    isOtherReasonSelected,
    isVerbafloClient,
    selectedSecondaryReason,
    reasonText,
  ]);

  const handleStageChange = useCallback(async () => {
    if (!isFormValid()) return;

    const reasonPayload = buildReasonPayload();

    try {
      await updateLeadStage({
        leadId: leadId,
        current_stage: newStage,
        ...reasonPayload,
      }).unwrap();
      trackEvents("lead_stage_changed", {
        from_stage: stageNameMap[currentStage] || currentStage,
        to_stage: stageNameMap[newStage] || newStage,
        changed_by: userId,
        change_time: new Date().toISOString(),
        org_id: orgId,
      });
      onOpenChange(false);
      onStageUpdate(newStage, reasonPayload);
      customToast("Lead stage updated successfully", "success");
    } catch (error) {
      console.log(error);
      trackEvents("lead_stage_change_failed", {
        from_stage: stageNameMap[currentStage] || currentStage,
        to_stage: stageNameMap[newStage] || newStage,
        changed_by: userId,
        error: error instanceof Error ? error.message : String(error),
        org_id: orgId,
      });
      onOpenChange(false);
      customToast("Failed to update lead stage", "error");
    }
  }, [
    isFormValid,
    buildReasonPayload,
    updateLeadStage,
    leadId,
    newStage,
    stageNameMap,
    currentStage,
    userId,
    orgId,
    onOpenChange,
    onStageUpdate,
  ]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
    onClose?.();
  }, [onOpenChange, onClose]);

  const stageDisplayName = stageNameMap[newStage] || newStage;

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/80 z-1000" />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-1001 rounded-[8px] shadow-popper-blur-effect backdrop-blur-[10px] min-w-[300px] max-w-[90vw] max-h-[90vh] overflow-visible bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk">
        <div className="p-6">
          <div className="w-[272px] flex flex-col gap-[36px]">
            <div className="flex flex-col gap-[16px]">
              <div className="flex flex-col gap-[2px]">
                <div className="flex items-center gap-[2px]">
                  <InactiveUserIcon className="w-[24px] h-[24px] text-gray-placeholder dark:text-gray-placeholder-dk" />
                  <h3 className="text-md font-semibold text-heading-text dark:text-heading-text-dk">
                    Move Lead to {stageDisplayName}?
                  </h3>
                </div>
                <p className="text-sm font-normal text-verba-field-label dark:text-verba-field-label-dk">
                  Are you sure you want to move this lead to{" "}
                  {stageDisplayName?.toLowerCase()}?
                </p>
                <div className="flex items-center gap-[8px]">
                  <div className="flex items-center justify-center gap-[6px] max-w-fit bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-[4px] py-[2px] px-[8px]">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{
                        backgroundColor: stageColorMap[currentStage],
                      }}
                    />
                    <p className="text-xs font-semibold text-verba-indigo dark:text-verba-indigo-dk uppercase">
                      {stageNameMap[currentStage]}
                    </p>
                  </div>
                  <RightArrowIcon className="w-[14px] h-[14px] text-verba-indigo dark:text-verba-indigo-dk" />
                  <div className="flex items-center justify-center gap-[6px] max-w-fit bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk rounded-[4px] py-[2px] px-[8px]">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: stageColorMap[newStage] }}
                    />
                    <p className="text-xs font-semibold text-verba-indigo dark:text-verba-indigo-dk uppercase">
                      {stageNameMap[newStage]}
                    </p>
                  </div>
                </div>
              </div>
              <hr className="w-full border-px border-border-gray dark:border-border-gray-dk" />
              <div className="flex flex-col gap-[12px]">
                <h3 className="text-md font-semibold text-heading-text dark:text-heading-text-dk">
                  Confirm {stageDisplayName} Status
                </h3>
                <SearchableDropdown
                  id="primary-reason-dropdown"
                  label="Primary Reason"
                  labelClassName="text-md font-normal text-verba-field-label dark:text-verba-field-label-dk"
                  options={primaryReasonOptions}
                  value={selectedPrimaryReason}
                  onChange={(value) => handlePrimaryReasonChange(value)}
                  placeholder={
                    !isVerbafloClient && isReasonsLoading
                      ? "Loading..."
                      : "Select"
                  }
                  ariaLabel="Select primary reason"
                  className="w-[265px]"
                  selectedOptionContainerClassName="w-full"
                  triggerClassName="w-full"
                  placeholderClassName="text-md font-normal text-gray-placeholder dark:text-gray-placeholder-dk"
                  dropdownMenuClassName="!max-h-[150px]"
                  sectionHeading="Primary Reasons"
                  disabled={!isVerbafloClient && isReasonsLoading}
                />
                {!isVerbafloClient &&
                  !isOtherReasonSelected &&
                  hasSecondaryOptions && (
                    <SearchableDropdown
                      id="secondary-reason-dropdown"
                      label="Secondary Reason"
                      labelClassName="text-md font-normal text-verba-field-label dark:text-verba-field-label-dk"
                      options={secondaryReasonOptions}
                      value={selectedSecondaryReason}
                      onChange={(value) => setSelectedSecondaryReason(value)}
                      placeholder="Select"
                      ariaLabel="Select secondary reason"
                      className="w-[265px]"
                      selectedOptionContainerClassName="w-full"
                      triggerClassName="w-full"
                      sectionHeading="Secondary Reasons"
                      placeholderClassName="text-md font-normal text-gray-placeholder dark:text-gray-placeholder-dk"
                      dropdownMenuClassName="!max-h-[150px]"
                      dropdownClassName="!w-max min-w-full"
                    />
                  )}
                {isOtherReasonSelected && (
                  <TextArea
                    id="secondary-reason-message"
                    label="Secondary Reason"
                    name="secondary-reason-message"
                    placeholder="Enter reason"
                    value={reasonText}
                    onChange={handleReasonTextChange}
                    labelClassName="text-md font-normal text-verba-field-label dark:text-verba-field-label-dk"
                    secondaryContainerClassName="px-[10px] py-[9px] rounded-[8px] bg-popper-bg dark:bg-popper-bg-dk border border-border-gray dark:border-border-gray-dk"
                  />
                )}
              </div>
            </div>
            <div className="flex items-center justify-end gap-[8px]">
              <Button
                onClick={handleCancel}
                innerText="Cancel"
                buttonContainerClass="w-[110px] py-[4px] border border-verba-indigo dark:border-verba-indigo-dk bg-transparent dark:bg-transparent rounded-[8px]"
                innerTextClass="text-verba-indigo dark:text-verba-indigo-dk text-md font-semibold"
              />
              <Button
                onClick={handleStageChange}
                isLoading={isUpdatingLeadStage}
                disabled={!isFormValid() || isUpdatingLeadStage}
                innerText={`Mark as ${stageDisplayName}`}
                buttonContainerClass="min-w-[110px] w-fit px-2 py-[4px] border border-verba-red-alert dark:border-verba-red-alert-dk bg-verba-red-alert dark:bg-verba-red-alert-dk rounded-[8px]"
                innerTextClass="text-fixed-white dark:text-fixed-white-dk text-md font-semibold"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LeadStageChangeToLostModal;
